import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AuthModal } from "@/components/auth/AuthModal"
import { ChatList } from "@/components/chat/ChatList"
import { isCurrentUserArtist } from "@/lib/chatData"
import { useAuthState } from "@/hooks/useAuth"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useArtistDashboardData } from "@/hooks/useArtistDashboardData"

export default function ArtistDashboardPage() {
  const { t } = useTranslation()
  const { user, loading } = useAuthState() // Add loading state
  const { trackAuthPrompt } = useAnalytics()
  const navigate = useNavigate()
  const [showLoginModal, setShowLoginModal] = useState(false)
  
  // Auto-show login modal immediately if user is not authenticated AND loading is complete
  useEffect(() => {
    if (!loading && !user) {
      trackAuthPrompt('artist_dashboard', 'auto_prompt')
      setShowLoginModal(true)
    }
  }, [user, loading, trackAuthPrompt]) // Add loading to dependencies

  // Clear modal if user becomes authenticated
  useEffect(() => {
    if (user) {
      setShowLoginModal(false)
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      return
    }

    const checkArtistStatus = async () => {
      try {
        const userIsArtist = await isCurrentUserArtist()
        
        // If user is not an artist, redirect them to regular chats
        if (!userIsArtist) {
          console.log('User is not an artist, redirecting to /chats')
          navigate('/chats', { replace: true })
        }
      } catch (error) {
        console.error('Error checking artist status:', error)
      }
    }

    checkArtistStatus()
  }, [user, navigate])

  const handleLoginSuccess = async () => {
    setShowLoginModal(false)
  }

  const { data: analyticsData, loading: analyticsLoading } = useArtistDashboardData(user?.uid || '', 7)

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        {/* Header */}
        <div className="phone-container-padded pt-4 pb-0">
          <div className="mb-3 flex items-center justify-between">
            <h1 className="text-page-title text-foreground">
              {t('artistDashboard.title')}
            </h1>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="phone-container-padded pb-4">
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                  Active Chats
                </h3>
                <p className="text-2xl font-bold text-foreground">
                  {analyticsData?.activeChats || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Current
                </p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                  Most Saved Flash
                </h3>
                {analyticsData?.mostSavedFlash ? (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={analyticsData.mostSavedFlash.imageUrl}
                        alt="Most saved flash"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground">
                        {analyticsData.mostSavedFlash.saves}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        saves
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No saved flashes
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Guest Waitlist Card */}
          <div className="mt-4">
            <button
              onClick={() => navigate('/guest-artists/waitlist')}
              className="w-full bg-primary/10 hover:bg-primary/20 rounded-lg p-4 transition-colors text-left"
            >
              <h3 className="text-sm font-medium text-primary mb-1">
                Guest Artist Waitlist
              </h3>
              <p className="text-xs text-muted-foreground">
                View requests from users who want you to guest in their city
              </p>
            </button>
          </div>
        </div>

        {/* Chat List */}
        <ChatList isArtistView={true} />
      </div>
      
      {/* Auto Login Modal - non-dismissible */}
      <AuthModal 
        isOpen={showLoginModal}
        onOpenChange={setShowLoginModal}
        dismissible={false}
        defaultMode="signup"
        source="artist_dashboard"
        onSuccess={handleLoginSuccess}
      />
    </>
  )
} 